import { HouseholdResponse } from '../../libs/gds-api-client';
import produce from 'immer';
import React from 'react';

import { Household } from './AddGuestRail';
import useShareTracking from './useShareTracking';

export type SeqIdParam = { seqId: string };

export interface SuccessResponse extends HouseholdResponse {
  deleted?: boolean;
}

export const emptyHousehold = {
  id: null,
  weddingId: null,
  description: null,
  groupId: null,
  rsvpNote: null,
  coupleNote: null,
  people: null,
  address: null,
  answers: null,
};

const emptyPerson = {
  id: null,
  firstName: null,
  lastName: null,
  isLeader: null,
  phone: null,
  email: null,
  invitations: null,
  preferences: null,
  __typename: 'GDS_Person' as 'GDS_Person',
};

const emptyInvitation = {
  id: null,
  eventId: null,
  rsvp: null,
  meal: null,
  gift: null,
  invitationSent: null,
  thankYouSent: null,
  answers: null,
  __typename: 'GDS_Invitation' as 'GDS_Invitation',
};

const emptyAddress = {
  street1: null,
  street2: null,
  city: null,
  state: null,
  zip: null,
  country: null,
  fullAddress: null,
  __typename: 'GDS_Address' as 'GDS_Address',
};

// Add __typename or apollo cache won't work
export const withTypename = (household: Household) => {
  const withTypename = produce(household, householdDraft => {
    if (householdDraft.people?.length) {
      householdDraft.people = householdDraft.people.map(person => {
        if (person.invitations?.length) {
          person.invitations = person.invitations.map(invitation => {
            if (invitation.answers?.length) {
              invitation.answers = invitation.answers.map(answer => {
                return { ...answer, __typename: 'GDS_Answer' };
              });
            }
            return { ...emptyInvitation, ...invitation };
          });
        }

        return { ...emptyPerson, ...person };
      });
    }
    householdDraft.address = {
      ...emptyAddress,
      ...household.address,
    };

    if (householdDraft.answers?.length) {
      householdDraft.answers = householdDraft.answers.map(answer => {
        return { ...answer, __typename: 'GDS_Answer' };
      });
    }

    householdDraft.__typename = 'GDS_Household';

    return householdDraft;
  });

  return { ...emptyHousehold, ...withTypename };
};

// We must remove the __typename field if present before we send to GDS.
export const cleanSetEditHousehold = (
  { __typename, ...household }: Household,
  setEditHousehold: React.Dispatch<React.SetStateAction<Household | undefined>>
) => {
  let newHousehold: Household = {
    ...household,
    people: household.people.map(({ __typename, ...p }) => {
      let preferences = null;

      if (p.preferences) {
        const { __typename: _, ...rest } = p.preferences || {};
        preferences = rest;
      }
      return {
        ...p,
        preferences,
        invitations: p.invitations?.map(({ __typename, ...i }) => ({
          ...i,
          answers: i.answers?.map(({ __typename, ...a }) => ({ ...a })) || null,
        })),
      };
    }),
    answers: household.answers?.map(({ __typename, ...a }) => ({ ...a })) || null,
  };

  if (newHousehold.address) {
    const { __typename, ...address } = newHousehold.address;
    newHousehold = {
      ...newHousehold,
      address,
    };
  }

  setEditHousehold(newHousehold);
};

// Removes the __typename field and null values before we send to GDS.
export const sanitizeCollectionForGDS = (collection: object[]) => {
  return collection.map(obj =>
    JSON.parse(
      JSON.stringify(obj, (key, value) => {
        return key === '__typename' ? undefined : value === null ? undefined : value;
      })
    )
  );
};

export const LOOKING_FOR_HOTELS_INFO_CARD = {
  title: 'Looking for hotels?',
  content: 'Keep the party together with room blocks.',
  footer: React.createElement(
    'div',
    { style: { paddingTop: '8px' } },
    'Powered by ',
    React.createElement('img', {
      src: 'https://media-api.xogrp.com/images/cbb83f7a-844a-4237-a285-74c6175c9bdc',
      alt: 'Engine',
      style: { height: '14px', verticalAlign: 'middle', display: 'inline' },
    })
  ),
  onClick: (track: ReturnType<typeof useShareTracking>, messageMedium: 'email' | 'sms' = 'email'): void => {
    track('Website Share Initiated', {
      action: 'select template',
      message_medium: messageMedium,
      message_type: 'Share local hotels',
    });
    window.open(
      'https://theknot.partners.engine.com/new-trip?utm_source=the-knot&utm_medium=channel&utm_ca[…]rm=tkguestlist&referral=MKT%20-%20Partner&rel=sponsored',
      '_blank'
    );
  },
  className: 'tanInfoCard',
  dataTestid: 'looking-for-hotels-info-card',
  ariaLabel: 'Looking for hotels?',
};
